import { useState, useEffect, useCallback } from 'react';
import { roaryApi } from '../api/modules/roary';
import type { MarshmallowUpdate } from '../api/modules/roary';

export interface MarshmallowState {
  remaining: number;
  total: number;
  resetDate: string | null;
  lastBurned: number;
  history: Array<{ sessionId: string; burned: number; date: string }>;
  isDepleted: boolean;
  isLoading: boolean;
}

const initialState: MarshmallowState = {
  remaining: 0,
  total: 0,
  resetDate: null,
  lastBurned: 0,
  history: [],
  isDepleted: false,
  isLoading: true,
};

export function useMarshmallows() {
  const [state, setState] = useState<MarshmallowState>(initialState);

  const fetchBalance = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true }));
      const data = await roaryApi.getMarshmallows();
      setState({
        remaining: data.remaining,
        total: data.total,
        resetDate: data.resetDate,
        lastBurned: 0,
        history: data.history,
        isDepleted: data.remaining <= 0,
        isLoading: false,
      });
    } catch (error) {
      console.error('Failed to fetch marshmallow balance:', error);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  // Fetch balance on mount
  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  // Called by useRoary when SSE marshmallow_update event arrives
  const onMarshmallowUpdate = useCallback((data: MarshmallowUpdate) => {
    setState(prev => ({
      ...prev,
      remaining: data.remaining,
      total: data.total,
      lastBurned: data.burned,
      isDepleted: data.remaining <= 0,
    }));
  }, []);

  // Called when marshmallow_depleted event arrives
  const onMarshmallowDepleted = useCallback(
    (data: { remaining: number; total: number; resetDate: string }) => {
      setState(prev => ({
        ...prev,
        remaining: 0,
        total: data.total,
        resetDate: data.resetDate,
        isDepleted: true,
      }));
    },
    []
  );

  // Clear the lastBurned after animation completes
  const clearLastBurned = useCallback(() => {
    setState(prev => ({ ...prev, lastBurned: 0 }));
  }, []);

  return {
    ...state,
    onMarshmallowUpdate,
    onMarshmallowDepleted,
    clearLastBurned,
    refetch: fetchBalance,
  };
}
